import { doc, setDoc, deleteField, runTransaction, serverTimestamp, collection, query, where, orderBy, limit, getDocs, Timestamp, writeBatch, updateDoc } from "firebase/firestore";
import db from "./firebase";
import { VoteParser } from "utils/VoteParser";

/**
* Создание новой комнаты
*/
export const createRoom = async (
  roomId: string,
  userId: string,
  userName: string,
  roomName: string,
  estimationType: string,
  mode: "withFacilitator" | "withoutFacilitator"
) => {
  const roomRef = doc(db, "rooms", roomId);
  await setDoc(roomRef, {
    roomType: "poker",
    roomName,
    estimationType,
    mode,
    owner: userId,
    participants: {
      [userId]: {
        id: userId,
        name: userName,
        role: mode === "withFacilitator" ? "facilitator" : "participant",
        estimate: null
      }
    },
    participantIds: [userId],
    isRevealed: false,
    estimates: {},
    createdAt: serverTimestamp()
  });
};

/**
* Создание новой bucket комнаты
*/
export const createBucketRoom = async (
  roomId: string,
  userId: string,
  userName: string,
  roomName: string,
  estimationType: string,
) => {
  const roomRef = doc(db, "rooms", roomId);

  await setDoc(roomRef, {
    roomType: "bucket",
    roomName,
    estimationType,
    owner: userId,
    participants: {
      [userId]: {
        id: userId,
        name: userName,
        role: "facilitator",
        estimate: null,
      },
    },
    participantIds: [userId],

    // bucket-поля
    setupStatus: "draft",               // draft | ready | inProgress | finished
    tasksPerIteration: 4,
    totalIterations: 0,
    currentIteration: null,

    createdAt: serverTimestamp(),
  });
};

/**
 * Присоединение к существующей комнате
 */
export const joinRoom = async (
  roomId: string,
  userId: string,
  userName: string,
  role: "facilitator" | "participant" = "participant"
) => {
  const roomRef = doc(db, "rooms", roomId);

  await runTransaction(db, async (transaction) => {
    const roomSnap = await transaction.get(roomRef);
    if (!roomSnap.exists()) {
      throw new Error("Room with id: " + roomId + " is not exist");
    }

    const data = roomSnap.data();
    const participants = data.participants || {};
    const participantIds: string[] = data.participantIds || [];

    // Do nothing if user already exist
    if (participants[userId]) {
      return;
    }

    const newParticipant = {
      id: userId,
      name: userName,
      role,
      estimate: null
    };

    const updatedParticipants = {
      ...participants,
      [userId]: newParticipant
    };

    const updatedParticipantIds = participantIds.includes(userId)
      ? participantIds
      : [...participantIds, userId];

    transaction.update(roomRef, { 
      participants: updatedParticipants, 
      participantIds: updatedParticipantIds  
    });
  });
};

/**
 * Удаление участника из комнаты
 */
export const removeParticipant = async (roomId: string, userId: string) => {
    const roomRef = doc(db, "rooms", roomId);

    await runTransaction(db, async (transaction) => {
        const roomSnap = await transaction.get(roomRef);
        if (!roomSnap.exists()) return;

        const data = roomSnap.data() as any;
        const participants = data.participants || {};
        const participantIds: string[] = data.participantIds || [];

        if (!participants[userId]) return;

        const updatedParticipantIds = participantIds.filter((id) => id !== userId);

        const updates: any = {
            [`participants.${userId}`]: deleteField(),
            [`estimates.${userId}`]: deleteField(),
            participantIds: updatedParticipantIds,
        };

        const removedRole = participants[userId]?.role;

        if (removedRole === "facilitator") {
            if (updatedParticipantIds.length > 0) {
                const newFacilitatorId = updatedParticipantIds[0];
                updates[`participants.${newFacilitatorId}.role`] = "facilitator";
            }
        }

        transaction.update(roomRef, updates);

        if (data.roomType === "bucket") {
            const iterationsSnap = await getDocs(collection(db, "rooms", roomId, "iterations"));

            for (const iter of iterationsSnap.docs) {
                const iterRef = doc(db, "rooms", roomId, "iterations", iter.id);
                const iterData = iter.data();

                const updateIter: any = {};

                if (iterData.estimatesByUser?.[userId]) {
                    updateIter[`estimatesByUser.${userId}`] = deleteField();
                }

                if (iterData.estimatesByTask) {
                    for (const taskId of Object.keys(iterData.estimatesByTask)) {
                        if (iterData.estimatesByTask[taskId]?.[userId] !== undefined) {
                            updateIter[`estimatesByTask.${taskId}.${userId}`] = deleteField();
                        }
                    }
                }

                if (Object.keys(updateIter).length > 0) {
                    transaction.update(iterRef, updateIter);
                }
            }
        }
    });
};

/**
 * Назначение фасилитатора в комнате
 * - userId получает роль "facilitator"
 * - всем остальным устанавливается "participant"
 * - если userId нет среди участников — ошибка
 */
export const assignFacilitator = async (roomId: string, userId: string) => {
  const roomRef = doc(db, "rooms", roomId);

  await runTransaction(db, async (transaction) => {
    const roomSnap = await transaction.get(roomRef);
    if (!roomSnap.exists()) {
      throw new Error("Room not found");
    }

    const data = roomSnap.data() || {};
    const participants = { ...(data.participants || {}) };

    if (!participants[userId]) {
      throw new Error(`User ${userId} is not a participant of room ${roomId}`);
    }

    // Обновляем роли
    Object.keys(participants).forEach((pid) => {
      participants[pid] = {
        ...participants[pid],
        role: pid === userId ? "facilitator" : "participant",
      };
    });

    transaction.update(roomRef, { participants });
  });
};

export interface RoomHistoryItem {
  id: string;
  roomType: "poker" | "bucket";
  roomName: string;
  estimationType: string;
  mode: string;
  participants: Record<string, {
    id: string;
    name: string;
    role: string;
    estimate: any;
  }>;
  participantIds: string[];
  createdAt: Timestamp;
  isRevealed: boolean;
  estimates: Record<string, any>;
}

export const getUserRoomHistory = async (userId: string): Promise<RoomHistoryItem[]> => {
  try {
    const q = query(
      collection(db, "rooms"),
      where("participantIds", "array-contains", userId),
      orderBy("createdAt", "desc"),
      limit(50)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      roomType: doc.data().roomType ?? "poker",
    })) as RoomHistoryItem[];
  } catch (error) {
    console.error("Error fetching room history:", error);
    return [];
  }
};

export interface BucketTaskDraft {
  id?: string;
  title: string;
  description?: string;
}

export const saveBucketSetup = async (params: {
  roomId: string;
  tasks: BucketTaskDraft[];
  tasksPerIteration: number;
}) => {
  const { roomId, tasks, tasksPerIteration } = params;

  const roomRef = doc(db, "rooms", roomId);
  const batch = writeBatch(db);

  // --- 1. Загружаем текущее состояние из базы
  const [tasksSnap, itersSnap] = await Promise.all([
    getDocs(collection(db, "rooms", roomId, "tasks")),
    getDocs(collection(db, "rooms", roomId, "iterations")),
  ]);

  const existingTasks = new Map(
    tasksSnap.docs.map((d) => [d.id, d.data() as any])
  );
  const existingIters = new Map(
    itersSnap.docs.map((d) => [d.id, { id: d.id, ...(d.data() as any) }])
  );

  // --- 2. Раскладываем новые таски по итерациям
  const chunks: BucketTaskDraft[][] = [];
  for (let i = 0; i < tasks.length; i += tasksPerIteration) {
    chunks.push(tasks.slice(i, i + tasksPerIteration));
  }

  // --- 3. Создаём/обновляем задачи и итерации
  let iterationIndex = 0;
  const newIterIds: string[] = [];
  const newTaskIds: string[] = [];

  for (const chunk of chunks) {
    const iterationId = `${iterationIndex}`;
    newIterIds.push(iterationId);

    const iterRef = doc(db, "rooms", roomId, "iterations", iterationId);

    const taskIds: string[] = [];
    chunk.forEach((t, idx) => {
      const taskId = t.id ?? crypto.randomUUID();
      newTaskIds.push(taskId);
      taskIds.push(taskId);

      const taskRef = doc(db, "rooms", roomId, "tasks", taskId);
      const existing = existingTasks.get(taskId);

      if (existing) {
        // обновляем только поля таски
        batch.update(taskRef, {
          title: t.title,
          description: t.description ?? "",
          iterationIndex: idx,
          iterationId,
        });
      } else {
        // создаём новую таску
        batch.set(taskRef, {
          title: t.title,
          description: t.description ?? "",
          createdBy: "system",
          createdAt: serverTimestamp(),
          iterationIndex: idx,
          iterationId,
        });
      }
    });

    const existingIter = existingIters.get(iterationId);
    if (existingIter) {
      // обновляем только taskIds и index
      batch.update(iterRef, {
        index: iterationIndex,
        taskIds,
      });
    } else {
      // создаём новую итерацию с дефолтами
      batch.set(iterRef, {
        index: iterationIndex,
        taskIds,
        status: "voting",
        revealedAt: null,
        estimatesByTask: {},
        estimatesByUser: {},
      });
    }

    iterationIndex++;
  }

  // --- 4. Удаляем лишние таски и итерации
  for (const taskId of existingTasks.keys()) {
    if (!newTaskIds.includes(taskId)) {
      batch.delete(doc(db, "rooms", roomId, "tasks", taskId));
    }
  }

  for (const iterId of existingIters.keys()) {
    if (!newIterIds.includes(iterId)) {
      batch.delete(doc(db, "rooms", roomId, "iterations", iterId));
    }
  }

  // --- 5. Обновляем саму комнату
  batch.update(roomRef, {
    tasksPerIteration,
    totalIterations: chunks.length,
  });

  await batch.commit();

  // --- 6. Если нет currentIteration — ставим "0"
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef);
    if (!snap.exists()) return;
    const data = snap.data() as any;

    const total = data.totalIterations ?? 0;
    const cur = data.currentIteration ?? null;
    const setupStatus = data.setupStatus === "draft" ? "ready" : data.setupStatus;

    // Needed to trigger free roam among iteration before voting start
    if (total > 0 && (cur === null || cur === undefined)) {
      tx.update(roomRef, { currentIteration: "0", setupStatus });
    }
  });
};

export const startBucketVoting = async (roomId: string) => {
  const firstIterationId = "0";
  const roomRef = doc(db, "rooms", roomId);
  await updateDoc(roomRef, {
    setupStatus: "inProgress",
    currentIteration: firstIterationId,
  });
};

export const setBucketCurrentIteration = async (roomId: string, iterationId: string) => {
  const roomRef = doc(db, "rooms", roomId);
  await updateDoc(roomRef, { currentIteration: iterationId });
};

export const revealBucketIteration = async (roomId: string, iterationId: string) => {
  const iterRef = doc(db, "rooms", roomId, "iterations", iterationId);
  await updateDoc(iterRef, { status: "revealed", revealedAt: serverTimestamp() });
};

export const finishBucket = async (roomId: string) => {
  const roomRef = doc(db, "rooms", roomId);
  await updateDoc(roomRef, { setupStatus: "finished" });
};

export const revoteBucketIteration = async (roomId: string, iterationId: string) => {
    const roomRef = doc(db, "rooms", roomId);
    const iterRef = doc(db, "rooms", roomId, "iterations", iterationId);

    await runTransaction(db, async (tx) => {
        const iterSnap = await tx.get(iterRef);
        if (!iterSnap.exists()) return;

        const updates: any = {
            status: "voting",
            revealedAt: null,
            estimatesByTask: {},
            estimatesByUser: {},
        };

        tx.update(iterRef, updates);

        tx.update(roomRef, {
            setupStatus: "inProgress",
            currentIteration: iterationId,
        });
    });
};

// запись голоса: атомарно в оба индекса
export const voteBucket = async (params: {
  roomId: string;
  iterationId: string;
  taskId: string;
  userId: string;
  value: number | string;
}) => {
  const { roomId, iterationId, taskId, userId, value } = params;
  const iterRef = doc(db, "rooms", roomId, "iterations", iterationId);

  const parsedValue = VoteParser.normalizeValue(value);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(iterRef);
    if (!snap.exists()) throw new Error("Iteration not found");
    const data = snap.data() as any;
    if (data.status !== "voting") return; // по правилам, после reveal голосовать нельзя

    const current = data?.estimatesByTask?.[taskId]?.[userId];
    if (current === parsedValue) return;

    tx.update(iterRef, {
      [`estimatesByTask.${taskId}.${userId}`]: parsedValue,
      [`estimatesByUser.${userId}.${taskId}`]: parsedValue,
    });
  });
};
